import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
  Stack,
  Button,
  Alert,
  Slide,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import {
  EventsTimeline,
  type TimelineEvent,
} from "../../../eventsTimelineComponent";
import { EmailPreview } from "../shipmentTimelineAndAutomation/EmailPreview";

interface SearchPanelProps {
  open: boolean;
  onClose: () => void;
}

export const SearchPanel: React.FC<SearchPanelProps> = ({ open, onClose }) => {
  const { apiClient } = useOutlookPlugin();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [previewEmailId, setPreviewEmailId] = useState<string | null>(null);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const response = await apiClient.post<{
        emailIds: string[];
        events: TimelineEvent[];
      }>("/email-automation/search-by-property", {
        query: searchQuery.trim(),
      });
      setEvents(response.events);
    } catch {
      setError("Search failed");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, apiClient]);

  const handleClose = useCallback(() => {
    setSearchQuery("");
    setEvents([]);
    setHasSearched(false);
    setError(null);
    setPreviewEmailId(null);
    onClose();
  }, [onClose]);

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "background.paper",
          zIndex: 1300,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Black header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ bgcolor: "#000", color: "#fff", px: 1, py: 1, flexShrink: 0 }}
        >
          <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Search
          </Typography>
        </Stack>

        {/* Search input + button */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ px: 1, pt: 1, alignItems: "center", flexShrink: 0 }}
        >
          <TextField
            size="small"
            placeholder="Search by MAWB, tracking number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
            autoFocus
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={loading || !searchQuery.trim()}
            sx={{ minWidth: 0, px: 2 }}
          >
            Search
          </Button>
        </Stack>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* No results */}
        {hasSearched && !loading && !error && events.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No emails found for this search.
            </Typography>
          </Box>
        )}

        {/* Events timeline */}
        {events.length > 0 && !loading && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <EventsTimeline
              dateColumnWidth={0}
              events={events}
              onOpenEmail={(emailId) => setPreviewEmailId(emailId)}
              showFilters={false}
              showStickyHeaders={true}
              numberOfLanes={2}
              topPadding={10}
              bottomPadding={20}
              bubbleWidth={70}
              laneSpacing={20}
              datePosition="center"
            />
          </Box>
        )}

        {previewEmailId && (
          <EmailPreview
            mode="readonly"
            emailId={previewEmailId}
            onDismiss={() => setPreviewEmailId(null)}
          />
        )}
      </Box>
    </Slide>
  );
};
